import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import type { UserCompanyGroupDto } from '@monorepo/shared';
import { BaseModal } from '../ui/BaseModal';
import { FormInput } from '../ui/FormInput';
import { useCreateGroup, useUpdateGroup } from '../../services/groups.service';

interface GroupModalProps {
  companyId: string;
}

export interface GroupModalRef {
  openCreate: () => void;
  openEdit: (group: UserCompanyGroupDto) => void;
}

interface GroupFormData {
  name: string;
}

export const GroupModal = forwardRef<GroupModalRef, GroupModalProps>(({ companyId }, ref) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<'create' | 'edit'>('create');
  const [selectedGroup, setSelectedGroup] = useState<UserCompanyGroupDto | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit: handleFormSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<GroupFormData>({
    defaultValues: {
      name: '',
    },
  });

  const createGroupMutation = useCreateGroup(companyId);
  const updateGroupMutation = useUpdateGroup(companyId);

  useImperativeHandle(ref, () => ({
    openCreate: () => {
      setMode('create');
      setSelectedGroup(null);
      setIsOpen(true);
    },
    openEdit: (group: UserCompanyGroupDto) => {
      setMode('edit');
      setSelectedGroup(group);
      setIsOpen(true);
    },
  }));

  useEffect(() => {
    if (mode === 'edit' && selectedGroup) {
      setValue('name', selectedGroup.name);
    } else {
      reset();
    }
  }, [mode, selectedGroup, isOpen, setValue, reset]);

  const handleSubmit = async (data: GroupFormData) => {
    setServerError(null);
    try {
      if (mode === 'create') {
        await createGroupMutation.mutateAsync({ name: data.name });
      } else if (mode === 'edit' && selectedGroup) {
        await updateGroupMutation.mutateAsync({
          groupId: selectedGroup.id,
          data: { name: data.name },
        });
      }
      handleClose();
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 409) {
        setServerError(t('admin.groups.modal.name.error.already_exists'));
      } else {
        console.error('Error submitting group modal:', error);
      }
    }
  };

  const handleClose = () => {
    reset();
    setServerError(null);
    setIsOpen(false);
  };

  const modalKey = mode === 'create' ? 'create_modal' : 'edit_modal';
  const isLoading = createGroupMutation.isPending || updateGroupMutation.isPending;

  const footer = (
    <div className="flex gap-4">
      <button
        type="button"
        onClick={handleClose}
        className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition"
        disabled={isLoading}
      >
        {t('admin.groups.modal.cancel')}
      </button>
      <button
        type="submit"
        form="group-form"
        className="flex-1 px-4 py-2 bg-primary text-white rounded-md hover:bg-opacity-90 transition disabled:opacity-50"
        disabled={isLoading}
      >
        {t(`admin.groups.${modalKey}.submit`)}
      </button>
    </div>
  );

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title={t(`admin.groups.${modalKey}.title`)}
      footer={footer}
      maxWidth="md"
    >
      <form id="group-form" onSubmit={handleFormSubmit(handleSubmit)} className="space-y-6">
        <div>
          <FormInput
            label={t('admin.groups.modal.name.label')}
            type="text"
            placeholder={t('admin.groups.modal.name.placeholder')}
            error={errors.name?.message || serverError || undefined}
            required
            {...register('name', {
              required: t('admin.groups.modal.name.error.required'),
              onChange: () => setServerError(null),
            })}
          />
          <p className="mt-2 text-sm text-gray-500">{t('admin.groups.modal.name.description')}</p>
        </div>
      </form>
    </BaseModal>
  );
});

GroupModal.displayName = 'GroupModal';
